import { useEffect, useRef, useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { Button } from "./ui/button";

export default function ScrollableTable({ children, label = "Tabela", count }) {
  const contentRef = useRef(null);
  const barRef = useRef(null);
  const reflectedBarLeft = useRef(null);
  const [scroll, setScroll] = useState({ width: 0, left: 0, maximum: 0 });

  useEffect(() => {
    const content = contentRef.current;
    if (!content) return;
    const measure = () => setScroll({
      width: content.scrollWidth,
      left: content.scrollLeft,
      maximum: Math.max(0, content.scrollWidth - content.clientWidth),
    });
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(content);
    if (content.firstElementChild) observer.observe(content.firstElementChild);
    return () => observer.disconnect();
  }, [children]);

  const synchronizeContent = (content) => {
    if (barRef.current && Math.abs(barRef.current.scrollLeft - content.scrollLeft) > 1) {
      reflectedBarLeft.current = content.scrollLeft;
      barRef.current.scrollLeft = content.scrollLeft;
    }
    setScroll((current) => ({ ...current, left: content.scrollLeft }));
  };
  const synchronizeBar = (bar) => {
    // Reflected scroll events must not interrupt the smooth arrow navigation.
    if (reflectedBarLeft.current !== null && Math.abs(bar.scrollLeft - reflectedBarLeft.current) <= 1) {
      reflectedBarLeft.current = null;
      return;
    }
    reflectedBarLeft.current = null;
    if (contentRef.current) contentRef.current.scrollLeft = bar.scrollLeft;
  };

  return <div className="min-w-0">
    <div className="sticky top-[64px] z-20 rounded-t-lg border border-slate-200 bg-white/95 px-4 py-2 backdrop-blur lg:top-0" data-testid="table-navigation">
      <div className="flex items-center justify-between gap-3">
        <span className="text-sm text-slate-600">{count !== undefined ? `${count} registros` : label}</span>
        {scroll.maximum > 1 && <div className="flex shrink-0 gap-1">
          <Button type="button" variant="ghost" size="icon" title="Mover tabela para a esquerda" aria-label="Mover tabela para a esquerda" disabled={scroll.left <= 1} onClick={() => contentRef.current?.scrollBy({ left: -360, behavior: "smooth" })}><ChevronLeft className="h-4 w-4" /></Button>
          <Button type="button" variant="ghost" size="icon" title="Mover tabela para a direita" aria-label="Mover tabela para a direita" disabled={scroll.left >= scroll.maximum - 1} onClick={() => contentRef.current?.scrollBy({ left: 360, behavior: "smooth" })}><ChevronRight className="h-4 w-4" /></Button>
        </div>}
      </div>
      {scroll.maximum > 1 && <div ref={barRef} className="overflow-x-auto overflow-y-hidden" onScroll={(event) => synchronizeBar(event.currentTarget)} aria-label={`Rolagem horizontal de ${label}`}><div style={{ width: `calc(100% + ${scroll.maximum}px)`, height: 12 }} /></div>}
    </div>
    <div ref={contentRef} className="overflow-x-auto rounded-b-lg border-x border-b border-slate-200 bg-white" tabIndex={0} role="region" aria-label={label} onScroll={(event) => synchronizeContent(event.currentTarget)}>{children}</div>
  </div>;
}
